import styled from 'styled-components';
import beTwMerge from '@/utils/beTwMerge';
import { IDataShowroom } from '@/components/BuilderIO/common/CommonFields';
import StoreLocatorCard from '@/components/StoreLocator/StoreLocatorCard/StoreLocatorCard';
import { GetHeadingLevel } from '@/components/BuilderIO/common/StoreLocator/utils';
import StoreLoader from './StoreLoader';

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media screen and (min-width: 1024px) {
    overflow: auto;
    scroll-snap-type: y mandatory;
    max-height: 341px;

    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export interface ITitle {
  content: string;
  titleHTMLTag?: 'h1' | 'h2';
}

interface IStoreLocatorCards {
  isLoading: boolean;
  builderShowroomsCopy?: IDataShowroom[];
  isMobile: boolean;
  showroomIndex: number;
  title: ITitle;
  titleLocationsNear: string;
  setShowroomModalInfo: (key: string, value: any) => void;
  bookAppointment: (showroom: IDataShowroom) => void;
  setShowroomIndex: (index: number) => void;
  setShowroomSelected: (item: IDataShowroom | any) => void;
  handleViewAllLocationCTA: (notFoundResults?: boolean) => void;
}

export default function StoreLocatorCards({
  isLoading,
  builderShowroomsCopy = [],
  isMobile,
  showroomIndex,
  title,
  titleLocationsNear,
  setShowroomModalInfo,
  bookAppointment,
  setShowroomIndex,
  setShowroomSelected,
  handleViewAllLocationCTA,
}: IStoreLocatorCards) {
  if (isLoading) {
    return (
      <div className="tw-min-h-[600px] lg:tw-min-h-[386px]">
        {!isMobile && (
          <div className="tw-mb-[20px]">
            <GetHeadingLevel
              mainLevel={title?.titleHTMLTag || 'h1'}
              currentLevel={1}
              asClass
              styles="tw-font-base tw-mb-0 tw-text-left !tw-text-[1rem] tw-font-medium min-[768px]:tw-m-0 tw-capitalize lg:!tw-text-[18px]"
            >
              {titleLocationsNear}
            </GetHeadingLevel>
          </div>
        )}
        <StoreLoader classnames="tw-bg-[#F9F9F9] tw-w-full lg:tw-w-[317px] tw-min-h-[600px] lg:tw-min-h-[342px]" />
      </div>
    );
  }

  return (
    <div className={beTwMerge(builderShowroomsCopy.length === 0 && !isMobile && 'tw-flex-1')}>
      {!isMobile && (
        <div className="tw-mb-[20px] tw-hidden lg:tw-block">
          <GetHeadingLevel
            mainLevel={title?.titleHTMLTag || 'h1'}
            currentLevel={1}
            asClass
            styles="tw-font-base tw-mb-0 tw-text-left !tw-text-[1rem] tw-font-medium min-[768px]:tw-m-0 tw-capitalize lg:!tw-text-[18px]"
          >
            {titleLocationsNear}
          </GetHeadingLevel>
        </div>
      )}

      <CardsContainer role="tablist" aria-label="tab list of locations near you">
        {builderShowroomsCopy.map((item: IDataShowroom, index: number) => (
          <StoreLocatorCard
            key={`${item?.displayName1}-${item?.displayName2}`}
            showroom={item}
            isSelected={showroomIndex === index}
            titleHTMLTag={title?.titleHTMLTag || 'h1'}
            index={index}
            handleBookAppointmentClick={() => {
              const showroomInfo = {
                modalName: `${item?.displayName1}${item?.displayName2 ? `- ${item?.displayName2}` : ''}`,
                displayName1: item?.displayName1 || '',
                displayName2: item?.displayName2 || '',
                addresLine1: item?.addressLine1 || '',
                addressLine2: item?.addressLine2 || '',
                setsterLocationId: item?.setsterLocationId || '',
                id: item?.id || '',
                city: item?.city || '',
                state: item?.state || '',
                zipCode: item?.zipCode || '',
                latitude: item?.latitude || '',
                longitude: item?.longitude || '',
              };
              setShowroomModalInfo('selectedShowroomStore', showroomInfo);
              setShowroomModalInfo('selectedtimezoneId', '0');

              bookAppointment(item as IDataShowroom);

              const isActive = showroomIndex === index;

              if (!isActive || !isMobile) {
                (window as any).dataLayer.push({
                  event: 'showroom_locator',
                  showroom_action: 'book setster appointment',
                  showroom_location: `${item.displayName1}${item.displayName2 ? ` - ${item.displayName2}` : ''}`,
                  showroom_page_section: 'list card',
                });
              }
            }}
            selectShowroom={event => {
              if (
                event?.target?.innerText === 'Book Appointment' ||
                event?.target?.innerText === 'Pre-Book Appointment'
              ) {
                return;
              }

              setShowroomIndex(index);
              setShowroomSelected(item);
              (window as any).dataLayer.push({
                event: 'showroom_locator',
                showroom_action: 'select store',
                showroom_location: `${item.displayName1}${item.displayName2 ? ` - ${item.displayName2}` : ''}`,
                showroom_page_section: 'list card',
              });
            }}
            isMobile={isMobile}
          />
        ))}

        {!isLoading && builderShowroomsCopy.length === 0 && (
          <p className="tw-hidden tw-mb-[1rem] tw-font-book lg:tw-block">
            We&apos;re sorry, there are no Brilliant Earth stores within 250 miles. Please try another search or{' '}
            <a
              className="tw-cursor-pointer tw-underline focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
              onClick={() => handleViewAllLocationCTA(true)}
              role="button"
              tabIndex={0}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleViewAllLocationCTA(true);
                }
              }}
            >
              View All Locations
            </a>
            .
          </p>
        )}

        {!isLoading && builderShowroomsCopy.length > 0 && (
          <a
            className="tw-mt-[8px] tw-hidden lg:tw-flex tw-cursor-pointer tw-underline tw-font-normal tw-leading-normal tw-text-center tw-capitalize tw-text-[14px] focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
            onClick={() => handleViewAllLocationCTA()}
            role="button"
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                handleViewAllLocationCTA();
              }
            }}
          >
            view all locations
          </a>
        )}
      </CardsContainer>
    </div>
  );
}
